import React, { useEffect } from 'react';

type Props = {
  open: boolean;
  /** Blob URL of the PDF (e.g. from URL.createObjectURL(blob)). Use #toolbar=0&navpanes=0 to hide browser PDF toolbar and download. */
  pdfUrl: string | null;
  title?: string;
  onClose: () => void;
};

/**
 * Full-screen style modal that displays a PDF in an iframe with toolbar hidden
 * (#toolbar=0&navpanes=0) so the browser's download button is not shown.
 * Caller should revoke the blob URL in onClose when done.
 */
const PdfViewerModal: React.FC<Props> = ({ open, pdfUrl, title = 'Meal Plan', onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const iframeSrc = pdfUrl
    ? `${pdfUrl}#toolbar=0&navpanes=0`
    : '';

  return (
    <div
      className="zivo-global-modal-overlay"
      onClick={onClose}
      role="presentation"
      style={{ alignItems: 'stretch', justifyContent: 'center', padding: '24px' }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '960px',
          height: '100%',
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="zivo-global-modal-button"
            style={{ padding: '8px 16px' }}
          >
            Close
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {iframeSrc ? (
            <iframe
              title={title}
              src={iframeSrc}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          ) : (
            <div style={{ padding: 24, color: '#6b7280' }}>No PDF to display.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfViewerModal;
